import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useGoals } from '../contexts/GoalContext';
import { useOKR } from '../contexts/OKRContext';
import { usePerformance } from '../contexts/PerformanceContext';
import { useNavigate } from 'react-router-dom';

const Dashboard = () => {
  const { user, logout } = useAuth();
  const { analytics: goalAnalytics } = useGoals();
  const { analytics: okrAnalytics, objectives } = useOKR();
  const { analytics: performanceAnalytics } = usePerformance();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getRoleColor = (role) => {
    const colors = {
      employee: 'bg-blue-100 text-blue-800',
      manager: 'bg-green-100 text-green-800',
      hr: 'bg-purple-100 text-purple-800',
      executive: 'bg-red-100 text-red-800',
      admin: 'bg-gray-100 text-gray-800'
    };
    return colors[role] || 'bg-gray-100 text-gray-800';
  };

  const getRoleFeatures = (role) => {
    const features = {
      employee: [
        'View and manage personal goals',
        'Complete self-assessments',
        'Participate in performance reviews',
        'Provide peer feedback',
        'Track OKR progress'
      ],
      manager: [
        'Conduct performance reviews',
        'Manage team goals and OKRs',
        'View team performance analytics',
        'Provide feedback to team members',
        'Review employee assessments'
      ],
      hr: [
        'Manage performance review cycles',
        'Create review templates',
        'Access company-wide analytics',
        'Monitor review completion',
        'Generate performance reports'
      ],
      executive: [
        'View organizational performance metrics',
        'Strategic goal alignment',
        'Executive performance dashboards',
        'Performance insights and trends',
        'Company-wide reporting'
      ],
      admin: [
        'Full system administration',
        'User and role management',
        'System configuration',
        'Performance data management',
        'Security and access control'
      ]
    };
    return features[role] || [];
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <h1 className="text-3xl font-bold text-gray-900">Performance Management System</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/goals')}
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Goals
              </button>
              <button
                onClick={() => navigate('/okrs')}
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                OKRs
              </button>
              <button
                onClick={() => navigate('/performance-reviews')}
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Reviews
              </button>
              {user?.role === 'hr' || user?.role === 'admin' ? (
                <button
                  onClick={() => navigate('/performance-analytics')}
                  className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
                >
                  Analytics
                </button>
              ) : null}
              <button
                onClick={() => navigate('/profile')}
                className="text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium"
              >
                Profile
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-md text-sm font-medium"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Welcome Section */}
          <div className="bg-white overflow-hidden shadow rounded-lg mb-8">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-16 w-16 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-xl font-bold">
                      {user?.first_name?.[0]}{user?.last_name?.[0]}
                    </span>
                  </div>
                </div>
                <div className="ml-4">
                  <h2 className="text-2xl font-bold text-gray-900">
                    Welcome, {user?.first_name} {user?.last_name}!
                  </h2>
                  <p className="text-gray-600">{user?.job_title} - {user?.department}</p>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(user?.role)}`}>
                    {user?.role?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 mb-8">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">🎯</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Goals</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {goalAnalytics?.total_goals || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">🚀</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">OKRs</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {okrAnalytics?.total_objectives || objectives?.length || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📊</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">OKR Score</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {okrAnalytics?.avg_score ? Math.round(okrAnalytics.avg_score * 100) + '%' : 'N/A'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📝</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Reviews</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {performanceAnalytics?.total_reviews || 0}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="p-5">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">⭐</span>
                    </div>
                  </div>
                  <div className="ml-5 w-0 flex-1">
                    <dl>
                      <dt className="text-sm font-medium text-gray-500 truncate">Perf Score</dt>
                      <dd className="text-lg font-medium text-gray-900">
                        {performanceAnalytics?.avg_overall_score ? 
                          Math.round(performanceAnalytics.avg_overall_score * 100) + '%' : 'N/A'}
                      </dd>
                    </dl>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg mb-8">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Quick Actions
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
                <button
                  onClick={() => navigate('/goals')}
                  className="bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-blue-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">🎯</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-blue-900">Manage Goals</h4>
                      <p className="text-xs text-blue-700">Create and track goals</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/okrs')}
                  className="bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-indigo-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">🚀</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-indigo-900">OKRs</h4>
                      <p className="text-xs text-indigo-700">Objectives & Key Results</p>
                    </div>
                  </div>
                </button>

                <button
                  onClick={() => navigate('/performance-reviews')}
                  className="bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-green-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">📝</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-green-900">Performance Reviews</h4>
                      <p className="text-xs text-green-700">View and complete reviews</p>
                    </div>
                  </div>
                </button>

                {(user?.role === 'hr' || user?.role === 'admin') && (
                  <button
                    onClick={() => navigate('/review-cycles')}
                    className="bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg p-4 text-left transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-purple-500 rounded-md flex items-center justify-center">
                        <span className="text-white text-sm font-medium">🔄</span>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-medium text-purple-900">Review Cycles</h4>
                        <p className="text-xs text-purple-700">Manage review cycles</p>
                      </div>
                    </div>
                  </button>
                )}

                <button
                  onClick={() => navigate('/profile')}
                  className="bg-orange-50 hover:bg-orange-100 border border-orange-200 rounded-lg p-4 text-left transition-colors"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-orange-500 rounded-md flex items-center justify-center">
                      <span className="text-white text-sm font-medium">👤</span>
                    </div>
                    <div className="ml-3">
                      <h4 className="text-sm font-medium text-orange-900">Profile</h4>
                      <p className="text-xs text-orange-700">Update your information</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Features Section */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                Available Features for {user?.role?.toUpperCase()} Role
              </h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {getRoleFeatures(user?.role).map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0">
                      <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    </div>
                    <p className="ml-3 text-sm text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Performance Overview */}
          {(performanceAnalytics || goalAnalytics || okrAnalytics) && (
            <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
              {/* Goal Completion */}
              {goalAnalytics && (
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="text-center">
                      <h3 className="text-lg leading-6 font-medium text-white mb-2">
                        Goal Completion Rate
                      </h3>
                      <div className="text-3xl font-bold text-white mb-2">
                        {goalAnalytics.completion_rate}%
                      </div>
                      <p className="text-blue-100">
                        {goalAnalytics.completed_goals} of {goalAnalytics.total_goals} goals completed
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* OKR Performance */}
              {okrAnalytics && (
                <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="text-center">
                      <h3 className="text-lg leading-6 font-medium text-white mb-2">
                        OKR Average Score
                      </h3>
                      <div className="text-3xl font-bold text-white mb-2">
                        {okrAnalytics.avg_score ? Math.round(okrAnalytics.avg_score * 100) : 0}%
                      </div>
                      <p className="text-indigo-100">
                        {okrAnalytics.total_objectives || 0} active objectives
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Performance Review Status */}
              {performanceAnalytics && (
                <div className="bg-gradient-to-r from-green-600 to-green-700 shadow rounded-lg">
                  <div className="px-4 py-5 sm:p-6">
                    <div className="text-center">
                      <h3 className="text-lg leading-6 font-medium text-white mb-2">
                        Review Completion Rate
                      </h3>
                      <div className="text-3xl font-bold text-white mb-2">
                        {performanceAnalytics.completion_rate}%
                      </div>
                      <p className="text-green-100">
                        {performanceAnalytics.completed_reviews} of {performanceAnalytics.total_reviews} reviews completed
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;